import React, { useMemo } from 'react';
import {
  Brain,
  Database,
  Activity,
  Search,
  Calculator,
  CheckSquare,
  BookOpen,
  MessageSquare,
  AlertCircle,
  User,
  FileText,
  Link,
  Send,
  Layers,
} from 'lucide-react';
import styles from './process-progress.module.css';

const STEP_CONFIG = {
  thinking: { label: 'Sending your message', icon: Send, iconName: 'send' },
  gathering_information: {
    label: 'Gathering information',
    icon: Database,
    iconName: 'database',
  },
  analyzing: { label: 'Analyzing the request', icon: Activity, iconName: 'activity' },
  searching_loan_info: {
    label: 'Searching loan information',
    icon: Search,
    iconName: 'search',
  },
  performing_calculation: {
    label: 'Performing calculations',
    icon: Calculator,
    iconName: 'calculator',
  },
  reviewing_results: {
    label: 'Reviewing results',
    icon: CheckSquare,
    iconName: 'check-square',
  },
  reading_documents: {
    label: 'Reading documents',
    icon: BookOpen,
    iconName: 'book-open',
  },
  preparing_response: {
    label: 'Preparing response',
    icon: MessageSquare,
    iconName: 'message-square',
  },
};

const ICON_MAP = {
  brain: Brain,
  database: Database,
  activity: Activity,
  search: Search,
  calculator: Calculator,
  'check-square': CheckSquare,
  'book-open': BookOpen,
  'message-square': MessageSquare,
  user: User,
  'file-text': FileText,
  link: Link,
  send: Send,
  layers: Layers,
};

const normalizeStatus = (status) => {
  if (!status) return 'pending';
  const normalized = String(status).toLowerCase();
  if (['pending', 'active', 'complete', 'error'].includes(normalized)) {
    return normalized;
  }
  return 'pending';
};

const buildFallbackSteps = () =>
  Object.entries(STEP_CONFIG).map(([id, config], index) => ({
    id,
    label: config.label,
    icon: config.iconName,
    status: 'pending',
    index,
  }));

const ProcessProgress = ({ processState, className = '', hideProgressBar = false }) => {
  const steps = useMemo(() => {
    if (processState?.steps?.length) {
      return [...processState.steps].sort((a, b) => {
        const aIndex = typeof a.index === 'number' ? a.index : 0;
        const bIndex = typeof b.index === 'number' ? b.index : 0;
        return aIndex - bIndex;
      });
    }
    return buildFallbackSteps();
  }, [processState]);

  const activeStep = useMemo(() => {
    const active = steps.find((step) => normalizeStatus(step.status) === 'active');
    if (active) {
      return active;
    }
    return steps[0] || null;
  }, [steps]);

  const isComplete = processState?.status === 'complete';

  const progressPercent = useMemo(() => {
    if (isComplete) return 100;
    const total = steps.length;
    if (total === 0) return 0;
    const completed = steps.filter(
      (step) => normalizeStatus(step.status) === 'complete'
    ).length;
    return Math.round((completed / total) * 100);
  }, [steps, isComplete]);

  return (
    <div className={`${styles.container} ${className}`} role="status" aria-live="polite">
      {activeStep ? (() => {
        const status = normalizeStatus(activeStep.status);
        // The surfaced step is the CURRENT in-progress stage: render it as
        // active (blue icon + running shimmer) unless it has actually
        // completed or errored. Keeps the animation going the whole time a
        // stage is shown — including the initial 'pending' seed placeholder
        // (e.g. "Sending your message") before the first live stage arrives.
        const displayStatus =
          status === 'complete' || status === 'error' ? status : 'active';
        const config = STEP_CONFIG[activeStep.id] || {};
        const Icon = ICON_MAP[activeStep.icon] || config.icon || AlertCircle;
        const isActive = displayStatus === 'active';

        return (
          <div
            key={activeStep.id}
            className={`${styles.step} ${styles[`status${displayStatus}`] || ''}`}
          >
            <Icon className={styles.stepIcon} aria-hidden="true" />
            <span
              className={`${styles.stepLabel} ${isActive ? styles.stepLabelActive : ''}`}
            >
              {activeStep.label || config.label || activeStep.id}
            </span>
          </div>
        );
      })() : null}
      {!hideProgressBar && (
        <div className={styles.progressTrack}>
          <div
            className={`${styles.progressFill} ${isComplete ? styles.progressComplete : ''}`}
            style={{ width: `${progressPercent}%` }}
            role="progressbar"
            aria-valuenow={progressPercent}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      )}
    </div>
  );
};

export default ProcessProgress;
